import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../context/GlobalState';
import { MdClose } from 'react-icons/md';
import { SelectGenre } from './Select/SelectGenre';
import { SelectLookingFor } from './Select/SelectLookingFor';
import { getLocalAvatar } from '../utils/avatarUtils';
import { createOrUpdateCard } from '../utils/createOrUpdateCard';
import { uploadImage } from '../utils/uploadImage';
import { API_BASE_URL } from '../utils/apiBaseUrl';

export const BandCardForm = ({ setCreateBandCard }) => {
  const { userId, loadBands, showNotification } = useContext(GlobalContext);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [bandData, setBandData] = useState({
    userId,
    name: '',
    genre: '',
    description: '',
    looking_for: [],
    userApplied: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    return () => {
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
    };
  }, [previewImageUrl]);

  const validateForm = () => {
    const { name, genre, description, looking_for } = bandData;
    const errors = {};

    if (!name) {
      errors.name = 'Band Name is required';
    }

    if (!genre) {
      errors.genre = 'Genre is required';
    }

    if (!description) {
      errors.description = 'Description is required';
    }

    if (looking_for.length === 0) {
      errors.looking_for = 'Role is required';
    }

    setErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (fieldName, value) => {
    setBandData(prevData => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleImageChange = event => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedImage(null);
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
      setPreviewImageUrl('');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setErrors(prevErrors => ({
        ...prevErrors,
        image: 'Please select a valid image file',
      }));
      setSelectedImage(null);
      if (previewImageUrl) {
        URL.revokeObjectURL(previewImageUrl);
      }
      setPreviewImageUrl('');
      return;
    }

    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
    }

    setPreviewImageUrl(URL.createObjectURL(file));

    setErrors(prevErrors => ({
      ...prevErrors,
      image: '',
    }));
    setSelectedImage(file);
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (validateForm()) {
      async function createCard() {
        setIsSubmitting(true);

        try {
          let image_url = getLocalAvatar(bandData.name);
          let uploadedImageId = null;

          if (selectedImage) {
            const uploadedImage = await uploadImage(selectedImage);
            image_url = uploadedImage.image_url;
            uploadedImageId = uploadedImage.uploadedImageId;
          }

          const data = { ...bandData, image_url, uploadedImageId };
          const response = await createOrUpdateCard(
            `${API_BASE_URL}/bands`,
            data,
            'POST',
          );

          if (response?.ok) {
            await loadBands(true);
            showNotification('Band card created successfully.', 'success');
            setTimeout(() => {
              setCreateBandCard(false);
            }, 1000);
          } else {
            showNotification('Could not create band card.', 'error');
          }
        } catch (error) {
          showNotification(error.message || 'Image upload failed.', 'error');
        } finally {
          setIsSubmitting(false);
        }
      }
      createCard();
    }
  };

  return (
    <form className='form-mypage' onSubmit={handleSubmit}>
      <MdClose
        className='button-close'
        onClick={() => {
          setCreateBandCard(false);
        }}
      />
      <div className='form-item'>
        <label htmlFor='name'>Band Name:</label>
        <input
          type='text'
          id='name'
          name='name'
          value={bandData.name}
          onChange={e => handleInputChange('name', e.target.value)}
        />
        {errors.name && <span className='message-error'>{errors.name}</span>}
      </div>
      <div className='form-item'>
        <label htmlFor='genre'>Genre:</label>
        <SelectGenre handleInputChange={handleInputChange} data={bandData} />
        {errors.genre && <span className='message-error'>{errors.genre}</span>}
      </div>
      <div className='form-item'>
        <label htmlFor='description'>Tell something:</label>
        <textarea
          id='description'
          name='description'
          value={bandData.description}
          onChange={e => handleInputChange('description', e.target.value)}
        />
        {errors.description && (
          <span className='message-error'>{errors.description}</span>
        )}
      </div>
      <div className='form-item'>
        <label htmlFor='looking_for'>Looking For:</label>
        <SelectLookingFor
          handleInputChange={handleInputChange}
          data={bandData}
        />
        {errors.looking_for && (
          <span className='message-error'>{errors.looking_for}</span>
        )}
      </div>
      <div className='form-item'>
        <label htmlFor='bandImage'>Photo (optional):</label>
        <div className='form-image-preview-wrap'>
          <img
            className='form-image-preview'
            src={previewImageUrl || getLocalAvatar(bandData.name)}
            alt='Band preview'
          />
        </div>
        <input
          type='file'
          id='bandImage'
          name='bandImage'
          accept='image/*'
          onChange={handleImageChange}
        />
        {errors.image && <span className='message-error'>{errors.image}</span>}
      </div>
      <button className='button-add' type='submit'>
        {isSubmitting ? 'Uploading...' : 'add'}
      </button>
    </form>
  );
};
